'use client';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/common/Button';
import { FormInput } from '@/components/forms/FormInput';
import { FormSelect } from '@/components/forms/FormSelect';
import { FormTextarea } from '@/components/forms/FormTextarea';
import { UserPlus, X } from 'lucide-react';

// Simplified sport-specific form data - no discriminated unions
export interface SportSpecificFormData {
  // Cricket fields
  playerType?: 'batsman' | 'bowler' | 'all-rounder' | 'wicket-keeper';
  battingStyle?: 'right-handed' | 'left-handed';
  bowlingStyle?:
    | 'right-arm-fast'
    | 'left-arm-fast'
    | 'right-arm-fast-medium'
    | 'left-arm-fast-medium'
    | 'right-arm-medium'
    | 'left-arm-medium'
    | 'right-arm-offbreak'
    | 'left-arm-orthodox'
    | 'right-arm-legbreak'
    | 'left-arm-unorthodox';
  preferredPosition?: string;

  // Football fields
  position?:
    | 'goalkeeper'
    | 'centre-back'
    | 'left-back'
    | 'right-back'
    | 'wing-back'
    | 'defensive-midfielder'
    | 'central-midfielder'
    | 'attacking-midfielder'
    | 'left-midfielder'
    | 'right-midfielder'
    | 'left-winger'
    | 'right-winger'
    | 'striker'
    | 'centre-forward'
    | 'false-nine'
    | 'second-striker';
  preferredFoot?: 'right' | 'left' | 'both';

  // Common fields
  playingStyle?: string;
  specialSkills?: string;
}

interface SportSpecificFormProps {
  sport: string;
  onSubmit: (data: SportSpecificFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  initialData?: SportSpecificFormData;
}

// Cricket form schema
const cricketSchema = z.object({
  playerType: z.enum(['batsman', 'bowler', 'all-rounder', 'wicket-keeper']),
  battingStyle: z.enum(['right-handed', 'left-handed']).optional(),
  bowlingStyle: z
    .enum([
      'right-arm-fast',
      'left-arm-fast',
      'right-arm-fast-medium',
      'left-arm-fast-medium',
      'right-arm-medium',
      'left-arm-medium',
      'right-arm-offbreak',
      'left-arm-orthodox',
      'right-arm-legbreak',
      'left-arm-unorthodox',
    ])
    .optional(),
  preferredPosition: z.string().optional(),
  playingStyle: z.string().optional(),
  specialSkills: z.string().optional(),
});

// Football form schema
const footballSchema = z.object({
  position: z
    .enum([
      'goalkeeper',
      'centre-back',
      'left-back',
      'right-back',
      'wing-back',
      'defensive-midfielder',
      'central-midfielder',
      'attacking-midfielder',
      'left-midfielder',
      'right-midfielder',
      'left-winger',
      'right-winger',
      'striker',
      'centre-forward',
      'false-nine',
      'second-striker',
    ])
    .optional(),
  preferredFoot: z.enum(['right', 'left', 'both']).optional(),
  playingStyle: z.string().optional(),
  specialSkills: z.string().optional(),
});

// Generic sport form schema
const genericSchema = z.object({
  specialSkills: z.string().optional(),
});

export function SportSpecificForm({
  sport,
  onSubmit,
  onCancel,
  isLoading = false,
  initialData,
}: SportSpecificFormProps) {
  const sportLower = sport.toLowerCase();

  const getSchema = () => {
    switch (sportLower) {
      case 'cricket':
        return cricketSchema;
      case 'football':
      case 'soccer':
        return footballSchema;
      default:
        return genericSchema;
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SportSpecificFormData>({
    resolver: zodResolver(getSchema()),
    defaultValues: initialData || {},
  });

  const handleFormSubmit = (data: SportSpecificFormData) => {
    onSubmit(data);
  };

  const renderCricketForm = () => (
    <div className="space-y-4">
      <FormSelect
        label="Player Type"
        name="playerType"
        register={register}
        error={errors.playerType?.message}
        options={[
          { value: '', label: 'Select player type' },
          { value: 'batsman', label: 'Batsman' },
          { value: 'bowler', label: 'Bowler' },
          { value: 'all-rounder', label: 'All-rounder' },
          { value: 'wicket-keeper', label: 'Wicket Keeper' },
        ]}
      />

      <FormSelect
        label="Batting Style"
        name="battingStyle"
        register={register}
        error={errors.battingStyle?.message}
        options={[
          { value: '', label: 'Select batting style' },
          { value: 'right-handed', label: 'Right-handed' },
          { value: 'left-handed', label: 'Left-handed' },
        ]}
      />

      <FormSelect
        label="Bowling Style"
        name="bowlingStyle"
        register={register}
        error={errors.bowlingStyle?.message}
        options={[
          { value: '', label: 'Select bowling style' },
          { value: 'right-arm-fast', label: 'Right-arm Fast' },
          { value: 'left-arm-fast', label: 'Left-arm Fast' },
          { value: 'right-arm-fast-medium', label: 'Right-arm Fast Medium' },
          { value: 'left-arm-fast-medium', label: 'Left-arm Fast Medium' },
          { value: 'right-arm-medium', label: 'Right-arm Medium' },
          { value: 'left-arm-medium', label: 'Left-arm Medium' },
          { value: 'right-arm-offbreak', label: 'Right-arm Off Break' },
          { value: 'left-arm-orthodox', label: 'Left-arm Orthodox' },
          { value: 'right-arm-legbreak', label: 'Right-arm Leg Break' },
          { value: 'left-arm-unorthodox', label: 'Left-arm Unorthodox' },
        ]}
      />

      <FormInput
        label="Preferred Position"
        name="preferredPosition"
        register={register}
        error={errors.preferredPosition?.message}
        placeholder="e.g., Opening batsman, Middle order, etc."
      />

      <FormInput
        label="Playing Style"
        name="playingStyle"
        register={register}
        error={errors.playingStyle?.message}
        placeholder="e.g., Aggressive, Defensive, Technical"
      />

      <FormTextarea
        label="Special Skills"
        name="specialSkills"
        register={register}
        error={errors.specialSkills?.message}
        placeholder="Any special skills or achievements"
        rows={3}
      />
    </div>
  );

  const renderFootballForm = () => (
    <div className="space-y-4">
      <FormSelect
        label="Position"
        name="position"
        register={register}
        error={errors.position?.message}
        options={[
          { value: '', label: 'Select position' },
          { value: 'goalkeeper', label: 'Goalkeeper' },
          { value: 'centre-back', label: 'Centre Back' },
          { value: 'left-back', label: 'Left Back' },
          { value: 'right-back', label: 'Right Back' },
          { value: 'wing-back', label: 'Wing Back' },
          { value: 'defensive-midfielder', label: 'Defensive Midfielder' },
          { value: 'central-midfielder', label: 'Central Midfielder' },
          { value: 'attacking-midfielder', label: 'Attacking Midfielder' },
          { value: 'left-midfielder', label: 'Left Midfielder' },
          { value: 'right-midfielder', label: 'Right Midfielder' },
          { value: 'left-winger', label: 'Left Winger' },
          { value: 'right-winger', label: 'Right Winger' },
          { value: 'striker', label: 'Striker' },
          { value: 'centre-forward', label: 'Centre Forward' },
          { value: 'false-nine', label: 'False Nine' },
          { value: 'second-striker', label: 'Second Striker' },
        ]}
      />

      <FormSelect
        label="Preferred Foot"
        name="preferredFoot"
        register={register}
        error={errors.preferredFoot?.message}
        options={[
          { value: '', label: 'Select preferred foot' },
          { value: 'right', label: 'Right' },
          { value: 'left', label: 'Left' },
          { value: 'both', label: 'Both' },
        ]}
      />

      <FormInput
        label="Playing Style"
        name="playingStyle"
        register={register}
        error={errors.playingStyle?.message}
        placeholder="e.g., Aggressive, Defensive, Technical, Pace, Dribbler"
      />

      <FormTextarea
        label="Special Skills"
        name="specialSkills"
        register={register}
        error={errors.specialSkills?.message}
        placeholder="Any special skills or achievements"
        rows={3}
      />
    </div>
  );

  const renderGenericForm = () => (
    <div className="space-y-4">
      <FormTextarea
        label="Special Skills"
        name="specialSkills"
        register={register}
        error={errors.specialSkills?.message}
        placeholder="Any special skills or achievements"
        rows={3}
      />
    </div>
  );

  const getSportSpecificForm = () => {
    const sportLower = sport.toLowerCase();
    switch (sportLower) {
      case 'cricket':
        return renderCricketForm();
      case 'football':
      case 'soccer':
        return renderFootballForm();
      default:
        return renderGenericForm();
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <div className="space-y-4">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900">
            {sport.charAt(0).toUpperCase() + sport.slice(1)} Player Details
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Please provide your sport-specific information to help team owners
            make informed decisions.
          </p>
        </div>

        {getSportSpecificForm()}
      </div>

      <div className="flex justify-end space-x-3 border-t border-gray-200 pt-4">
        <Button
          type="button"
          variant="light"
          onClick={onCancel}
          disabled={isLoading}
          leftIcon={<X className="h-4 w-4" />}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          isLoading={isLoading}
          leftIcon={<UserPlus className="h-4 w-4" />}
        >
          Complete Enrollment
        </Button>
      </div>
    </form>
  );
}
